import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {CheckCircle, XCircle} from 'lucide-react'
import { lumi } from '../lib/lumi'

interface PropostaData {
  _id: string
  nomeCliente: string
  servicoOferecido: string
  descricaoServico: string
  prazo: string
  valor: string
  formaPagamento: string
  dataCriacao?: string
  logoEmpresa?: string
  nomeEmpresa: string
  corPrincipal: string
  prazoExpiracao: string
  status?: string
  userId: string
}

export default function PropostaPublica() {
  const { id } = useParams()
  const [proposta, setProposta] = useState<PropostaData | null>(null)
  const [expirada, setExpirada] = useState(false)
  const [aceita, setAceita] = useState(false)
  const [recusada, setRecusada] = useState(false)
  const [nomeCliente, setNomeCliente] = useState('')
  const [emailCliente, setEmailCliente] = useState('')
  const [cpfCnpj, setCpfCnpj] = useState('')
  const [mostrarFormulario, setMostrarFormulario] = useState(false)

  useEffect(() => {
    const loadProposta = async () => {
      try {
        if (!id) return
        
        const result = await lumi.entities.propostas.list({
          filter: { _id: id }
        })
        
        if (result.list && result.list.length > 0) {
          const propostaEncontrada = result.list[0]
          setProposta(propostaEncontrada as any)
          
          const dataExpiracao = new Date(propostaEncontrada.prazoExpiracao)
          const agora = new Date()
          if (agora > dataExpiracao) {
            setExpirada(true)
          }
        }
      } catch (error) {
        console.error('Erro ao carregar proposta:', error)
      }
    }
    loadProposta()
  }, [id])

  const handleAceitarClick = () => {
    setMostrarFormulario(true)
  }

  const handleConfirmarAceite = async () => {
    if (!nomeCliente || !emailCliente || !cpfCnpj) {
      alert('Por favor, preencha todos os campos')
      return
    }

    try {
      if (!proposta?._id) return
      
      await lumi.entities.propostas.update(proposta._id, {
        status: 'aceita',
        nomeClienteAceite: nomeCliente,
        emailClienteAceite: emailCliente,
        cpfCnpjCliente: cpfCnpj,
        dataAceite: new Date().toISOString()
      })
      
      setAceita(true)
    } catch (error) {
      console.error('Erro ao aceitar proposta:', error)
      alert('Erro ao aceitar proposta')
    }
  }

  const handleRecusar = async () => {
    try {
      if (!proposta?._id) return
      
      await lumi.entities.propostas.update(proposta._id, {
        status: 'perdida',
        dataRecusa: new Date().toISOString()
      })
      
      setRecusada(true)
    } catch (error) {
      console.error('Erro ao recusar proposta:', error)
      alert('Erro ao recusar proposta')
    }
  }

  if (!proposta) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <p className="text-gray-600 text-sm">Carregando...</p>
      </div>
    )
  }

  if (expirada) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <XCircle size={48} className="text-red-700 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-900 mb-2">Proposta Expirada</h1>
          <p className="text-sm text-gray-600">
            Esta proposta expirou em {new Date(proposta.prazoExpiracao).toLocaleDateString('pt-BR')}
          </p>
        </div>
      </div>
    )
  }

  if (aceita) {
    return (
      <div className="min-h-screen bg-white py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-200">
            {proposta.logoEmpresa && (
              <img 
                src={proposta.logoEmpresa} 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
            )}
            <h1 className="text-lg font-bold text-gray-900">
              {proposta.nomeEmpresa}
            </h1>
          </div>

          <div className="text-center py-12">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-3">Proposta Aceita!</h2>
            <p className="text-base text-gray-700 mb-2">
              <span className="font-bold">{proposta.nomeEmpresa}</span> já irá lhe enviar um link de pagamento.
            </p>
            <p className="text-sm text-gray-600 mt-3">Obrigado por aceitar nossa proposta.</p>
          </div>

          <div className="flex justify-end pt-6 opacity-40">
            <img 
              src="https://static.lumi.new/94/94387bd69277769ec0567b4f6dbc1e0e.webp" 
              alt="Unovalinks" 
              className="h-8 w-auto"
            />
          </div>
        </div>
      </div>
    )
  }

  if (recusada) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <XCircle size={48} className="text-gray-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-900 mb-2">Proposta Recusada</h1>
          <p className="text-sm text-gray-600">Obrigado pelo seu tempo.</p>
        </div>
      </div>
    )
  }

  if (mostrarFormulario) {
    return (
      <div className="min-h-screen bg-white py-8 px-4">
        <div className="max-w-xl mx-auto">
          <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-200">
            {proposta.logoEmpresa && (
              <img 
                src={proposta.logoEmpresa} 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
            )}
            <h1 className="text-lg font-bold text-gray-900">
              {proposta.nomeEmpresa}
            </h1>
          </div>

          <h2 className="text-lg font-bold text-gray-900 mb-6">Confirme seus dados</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-900 mb-1">Nome Completo</label>
              <input
                type="text"
                value={nomeCliente}
                onChange={(e) => setNomeCliente(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-gray-900 focus:outline-none"
                placeholder="Seu nome completo"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-900 mb-1">E-mail</label>
              <input
                type="email"
                value={emailCliente}
                onChange={(e) => setEmailCliente(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-gray-900 focus:outline-none"
                placeholder="seu@email.com"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-900 mb-1">CPF/CNPJ</label>
              <input
                type="text"
                value={cpfCnpj}
                onChange={(e) => setCpfCnpj(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-gray-900 focus:outline-none"
                placeholder="000.000.000-00"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setMostrarFormulario(false)}
                className="flex-1 px-4 py-2 text-sm border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
              >
                Voltar
              </button>
              <button
                onClick={handleConfirmarAceite}
                className="flex-1 px-4 py-2 text-sm bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800"
              >
                Confirmar e Aceitar
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const descricaoItens = proposta.descricaoServico.split('\n').filter(item => item.trim())

  return (
    <div className="min-h-screen bg-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-200">
          {proposta.logoEmpresa && (
            <img 
              src={proposta.logoEmpresa} 
              alt="Logo" 
              className="h-12 w-12 object-contain"
            />
          )}
          <h1 className="text-lg font-bold text-gray-900">
            {proposta.nomeEmpresa}
          </h1>
        </div>

        {/* Nome do Serviço e Prazo */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            {proposta.servicoOferecido} <span className="text-xl text-gray-600">({proposta.prazo})</span>
          </h2>
        </div>

        {/* Descrição */}
        <div className="mb-6">
          <h3 className="text-sm font-bold text-gray-900 mb-3">descrição do serviço</h3>
          <ul className="space-y-2">
            {descricaoItens.map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-gray-700 text-sm">
                <span className="text-base mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Valor */}
        <div className="mb-6">
          <h3 className="text-sm font-bold text-gray-900 mb-2">valor</h3>
          <p className="text-4xl font-bold text-red-700">
            R$ {proposta.valor}
          </p>
        </div>

        {/* Data */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <p className="text-xs text-gray-600 mb-1">data de proposta feita</p>
          <p className="text-sm font-semibold text-gray-900">
            {new Date(proposta.dataCriacao || '').toLocaleDateString('pt-BR')}
          </p>
        </div>

        {/* Botões */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <button
            onClick={handleAceitarClick}
            className="flex-1 px-6 py-3 text-sm border-2 border-black text-black font-bold rounded-lg hover:bg-black hover:text-white transition-all"
          >
            aceitar
          </button>
          <button
            onClick={handleRecusar}
            className="flex-1 px-6 py-3 text-sm border-2 border-black text-black font-bold rounded-lg hover:bg-black hover:text-white transition-all"
          >
            recusar
          </button>
        </div>

        {/* Logo Unovalinks */}
        <div className="flex justify-end opacity-40">
          <img 
            src="https://static.lumi.new/94/94387bd69277769ec0567b4f6dbc1e0e.webp" 
            alt="Unovalinks" 
            className="h-8 w-auto"
          />
        </div>
      </div>
    </div>
  )
}
